import React, { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { KSpan, KTitleSpan } from "kahuna-base-react-components"
import { allPlatformOptions } from "../../../../constants"
import { getSelectedLanguage, lang } from "../../../../constants/languages"
import { UpcomingTotals } from "../../../../types"

type PlatformRow = {
  key: string
  label: string
  icon?: string
  complete: boolean
  order: number
}

type PeriodGroup = {
  period: string
  rows: PlatformRow[]
}

type StatusTab = "all" | "complete" | "missing"

// backend sends the platform lists as flat strings, either "202608:" (period header)
// or "spo-spotify, apl-apple, ..." (comma separated raw platform keys)
const PERIOD_HEADER_REGEX = /^(\d{6}):$/

// resolved lazily: a module level reduce would break if constants/index is still
// initialising when this module is first evaluated
let platformMetaMap: { [key: string]: { label: string; icon?: string; order: number } } | undefined

const getPlatformMeta = (platform: string) => {
  if (!platformMetaMap) {
    platformMetaMap = (allPlatformOptions || []).reduce((map, option, index) => {
      if (option.value2) {
        map[option.value2] = { label: option.label, icon: option.icon, order: index }
      }
      return map
    }, {} as { [key: string]: { label: string; icon?: string; order: number } })
  }
  return platformMetaMap[platform]
}

const periodLabel = (period: string, fallback: string) => {
  if (!PERIOD_HEADER_REGEX.test(`${period}:`)) return fallback
  const year = parseInt(period.slice(0, 4), 10)
  const month = parseInt(period.slice(4, 6), 10)
  if (isNaN(year) || isNaN(month)) return fallback
  return new Date(year, month - 1, 1)
    .toLocaleDateString(getSelectedLanguage(), { month: "long", year: "numeric" })
    .toUpperCase()
}

export const buildPeriodGroups = (upcomingTotal: UpcomingTotals): PeriodGroup[] => {
  const groups: { [period: string]: { complete: string[]; missing: string[] } } = {}
  const periods: string[] = []

  const ensureGroup = (period: string) => {
    if (!groups[period]) {
      groups[period] = { complete: [], missing: [] }
      periods.push(period)
    }
    return groups[period]
  }

  const collect = (list: string[] | undefined, field: "complete" | "missing") => {
    let currentPeriod = ""
    ;(list || []).forEach((item) => {
      const headerMatch = (item || "").match(PERIOD_HEADER_REGEX)
      if (headerMatch) {
        currentPeriod = headerMatch[1]
        ensureGroup(currentPeriod)
        return
      }
      const platforms = (item || "")
        .split(",")
        .map((platform) => platform.trim())
        .filter((platform) => platform.length > 0)
      if (platforms.length === 0) return
      ensureGroup(currentPeriod)[field].push(...platforms)
    })
  }

  collect(upcomingTotal.platformCompleteList, "complete")
  collect(upcomingTotal.platformMissingList, "missing")

  return periods.sort().map((period) => {
    const seen = new Set<string>()
    const rows: PlatformRow[] = []
    const addRows = (platforms: string[], complete: boolean) => {
      platforms.forEach((platform) => {
        if (seen.has(platform)) return
        seen.add(platform)
        const meta = getPlatformMeta(platform)
        rows.push({
          key: platform,
          label: meta?.label || platform,
          icon: meta?.icon,
          complete,
          // backend derives the missing list from a python set, so its order is arbitrary
          // and changes between requests. sort by the canonical platform order instead.
          order: meta?.order ?? allPlatformOptions.length
        })
      })
    }
    addRows(groups[period].complete, true)
    addRows(groups[period].missing, false)
    rows.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
    return { period, rows }
  })
}

const StatusBadge = ({ complete }: { complete: boolean }) => (
  <div
    className="flex items-center shrink-0 px-2 py-[3px] rounded-full"
    style={{ backgroundColor: complete ? "#E7F8EC" : "#FDE9E9" }}
  >
    <KSpan
      text={complete ? lang.royalties.upcoming.widget.complete : lang.royalties.upcoming.widget.awaiting_data}
      fontSize={12}
      lineHeight="16px"
      fontWeight={500}
      color={complete ? "#12874A" : "#D22F2F"}
    />
  </div>
)

const TabButton = ({
  active,
  label,
  dotColor,
  onClick
}: {
  active: boolean
  label: string
  dotColor?: string
  onClick: () => void
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
    style={{
      backgroundColor: active ? "#F0F0F0" : "transparent",
      border: `1px solid ${active ? "#E0E0E0" : "#EDEDED"}`
    }}
  >
    {dotColor && <div className="w-[6px] h-[6px] rounded-full shrink-0" style={{ backgroundColor: dotColor }} />}
    <KSpan text={label} fontSize={12} lineHeight="16px" fontWeight={500} color={active ? "#111" : "#666"} />
  </button>
)

const PlatformStatusDrawer = ({ upcomingTotal, onClose }: { upcomingTotal: UpcomingTotals; onClose: () => void }) => {
  const [selectedTab, setSelectedTab] = useState<StatusTab>("all")
  const drawerRef = useRef<HTMLDivElement>(null)

  // opened on hover, so it must stay put until dismissed explicitly
  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        onClose()
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    document.addEventListener("mousedown", onMouseDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("mousedown", onMouseDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [onClose])

  const groups = useMemo(() => buildPeriodGroups(upcomingTotal), [upcomingTotal])

  const { completeCount, missingCount } = useMemo(() => {
    return groups.reduce(
      (counts, group) => {
        group.rows.forEach((row) => (row.complete ? (counts.completeCount += 1) : (counts.missingCount += 1)))
        return counts
      },
      { completeCount: 0, missingCount: 0 }
    )
  }, [groups])

  const paymentDateLabel = upcomingTotal.paymentDate?.includes("Q")
    ? upcomingTotal.paymentDate
    : periodLabel(upcomingTotal.paymentDate, upcomingTotal.paymentDate)

  const visibleGroups = groups
    .map((group) => ({
      ...group,
      rows: group.rows.filter((row) => selectedTab === "all" || (selectedTab === "complete") === row.complete)
    }))
    // an empty group means the reporting period has not started yet, keep it visible on the "all" tab
    .filter((group) => group.rows.length > 0 || (selectedTab === "all" && group.period !== ""))

  // if the backend ever changes the shape of the flat lists, degrade to the raw entries
  // instead of rendering a panel with no platforms in it
  const rawEntries = [...(upcomingTotal.platformCompleteList || []), ...(upcomingTotal.platformMissingList || [])]
    .map((entry) => (entry || "").trim())
    .filter((entry) => entry.length > 0)
  const parseFailed = completeCount + missingCount === 0 && rawEntries.length > 0

  return createPortal(
    <div
      ref={drawerRef}
      className="fixed top-0 right-0 bottom-0 flex flex-col bg-[#fff]"
      style={{
        width: 420,
        maxWidth: "92vw",
        zIndex: 1000000,
        borderLeft: "1px solid #EDEDED",
        boxShadow: "-8px 0 24px rgba(17, 17, 17, 0.08)"
      }}
    >
      <div className="px-5 pt-5 pb-4 shrink-0">
        <div className="flex items-start justify-between gap-3">
          <KTitleSpan
            fontSize={20}
            lineHeight="26px"
            color="#111"
            text={`${paymentDateLabel} ${lang.royalties.upcoming.widget.platform_status_title}`}
          />
          <button
            type="button"
            onClick={onClose}
            aria-label="close"
            className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#F0F0F0]"
          >
            <KSpan text="✕" fontSize={14} lineHeight="14px" color="#666" />
          </button>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <TabButton
            active={selectedTab === "all"}
            label={lang.royalties.upcoming.widget.all}
            onClick={() => setSelectedTab("all")}
          />
          <TabButton
            active={selectedTab === "complete"}
            dotColor="#12874A"
            label={`${lang.royalties.upcoming.widget.complete} (${completeCount})`}
            onClick={() => setSelectedTab("complete")}
          />
          <TabButton
            active={selectedTab === "missing"}
            dotColor="#D22F2F"
            label={`${lang.royalties.upcoming.widget.missing} (${missingCount})`}
            onClick={() => setSelectedTab("missing")}
          />
        </div>
      </div>
      <div className="grow overflow-y-auto min-h-0" style={{ borderTop: "1px solid #F0F0F0" }}>
        {parseFailed &&
          rawEntries.map((entry, index) => (
            <div key={`platform_status_raw_${index}`} className="px-5 py-1.5">
              <KSpan text={entry} fontSize={13} lineHeight="18px" color="#111" />
            </div>
          ))}
        {!parseFailed && visibleGroups.length === 0 && (
          <div className="px-5 py-5">
            <KSpan text={lang.royalties.upcoming.widget.no_platforms} fontSize={13} color="#666" />
          </div>
        )}
        {visibleGroups.map((group) => (
          <div key={`platform_status_group_${group.period || "default"}`}>
            {/* monthly accounts get no period headers from the backend, so the single group
                would repeat the title verbatim */}
            {!(visibleGroups.length === 1 && group.period === "") && (
              <div
                className="flex items-center justify-between px-5 py-2 sticky top-0"
                style={{ backgroundColor: "#F7F7F7", borderBottom: "1px solid #EDEDED" }}
              >
                <KSpan
                  text={periodLabel(group.period, paymentDateLabel)}
                  fontSize={12}
                  lineHeight="16px"
                  fontWeight={600}
                  letterSpacing="0.4px"
                  color="#666"
                />
                {group.rows.length === 0 && (
                  <KSpan
                    text={lang.royalties.upcoming.widget.active_list}
                    fontSize={12}
                    lineHeight="16px"
                    color="#999"
                  />
                )}
              </div>
            )}
            {group.rows.map((row) => (
              <div
                key={`platform_status_row_${group.period}_${row.key}`}
                className="flex items-center justify-between gap-3 px-5 py-2.5"
                style={{ borderBottom: "1px solid #F5F5F5" }}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  {row.icon ? (
                    <img className="w-5 h-5 shrink-0 object-contain" src={row.icon} alt={row.label} />
                  ) : (
                    <div className="w-5 h-5 shrink-0 rounded-full" style={{ backgroundColor: "#E5E5E5" }} />
                  )}
                  <KSpan text={row.label} fontSize={14} lineHeight="20px" color="#111" ellipsis />
                </div>
                <StatusBadge complete={row.complete} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>,
    document.body
  )
}

export default PlatformStatusDrawer
